import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { media } from '../../styles/media';

// --- Import MUI Icons ---
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import BrushIcon from '@mui/icons-material/Brush';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'; // Example for NFT
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import TranslateIcon from '@mui/icons-material/Translate';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CodeIcon from '@mui/icons-material/Code';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import BookIcon from '@mui/icons-material/Book';
import PaletteIcon from '@mui/icons-material/Palette';
import ComputerIcon from '@mui/icons-material/Computer';
import EventIcon from '@mui/icons-material/Event';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
// ------------------------

// Map icon keys to actual components
const iconMap = {
  ReceiptLongIcon,
  VideogameAssetIcon,
  BrushIcon,
  PhotoCameraIcon,
  SmartphoneIcon,
  TranslateIcon,
  AccountBalanceWalletIcon,
  SupportAgentIcon,
  CodeIcon,
  ShoppingCartIcon,
  MusicNoteIcon,
  FlightTakeoffIcon,
  LocalOfferIcon,
  SchoolIcon,
  BuildIcon,
  FitnessCenterIcon,
  FastfoodIcon,
  BookIcon,
  PaletteIcon,
  ComputerIcon,
  EventIcon,
  SecurityIcon,
  PublicIcon,
  GavelIcon,
  HomeWorkIcon,
};

// Анимации
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-100%); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-100%); }
`;

// Стили
const FeedWrapper = styled.div`
  position: fixed;
  top: 20px; // Немного ниже
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px); // Чуть больше отступы
  max-width: 480px; // Немного шире
  z-index: 1050;
  pointer-events: none;

  ${media.sm`
    top: 90px; // Увеличиваем отступ сверху для мобильных, чтобы не перекрывать хедер
    width: calc(100% - 30px);
  `}
`;

const NotificationBox = styled.div`
  pointer-events: all;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  color: #2B2D42;
  padding: 14px 20px;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  animation: ${props => props.$isVisible ? slideDown : slideUp} 0.4s ease-out forwards;
  opacity: ${props => props.$isVisible ? 1 : 0};
  backdrop-filter: blur(5px);
  border: 1px solid rgba(0, 0, 0, 0.06);

  svg {
    font-size: 22px;
    color: #FF4B8F;
    flex-shrink: 0;
  }
`;

const NotificationText = styled.p`
  margin: 0;
  font-size: 14.5px;
  line-height: 1.5;
  font-weight: 500;

  strong {
    font-weight: 700;
    color: #000;
  }
`;

// Компонент
const ActivityFeed = () => {
  const { t } = useTranslation('translation');
  const [currentDeal, setCurrentDeal] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // --- Расширенные Симулированные данные (25 вариантов) ---
  const simulatedDeals = useMemo(() => [
    { seller: 'CryptoGamer', itemKey: 'dealItem.gameKey', value: 15, crypto: 'USDT', icon: 'VideogameAssetIcon' },
    { seller: 'ArtFactory', itemKey: 'dealItem.logoDesign', value: 0.2, crypto: 'BNB', icon: 'BrushIcon' }, // ETH -> BNB
    { seller: 'PixelDreams', itemKey: 'dealItem.nftArt', value: 250, crypto: 'TON', icon: 'PhotoCameraIcon' },
    { seller: 'GadgetHub', itemKey: 'dealItem.smartphone', value: 3, crypto: 'SOL', icon: 'SmartphoneIcon' }, // BTC -> SOL
    { seller: 'LinguaPro', itemKey: 'dealItem.docTranslation', value: 55, crypto: 'USDT', icon: 'TranslateIcon' },
    { seller: 'LeatherCraft', itemKey: 'dealItem.handmadeWallet', value: 70, crypto: 'SOL', icon: 'AccountBalanceWalletIcon' },
    { seller: 'ExpertAdvice', itemKey: 'dealItem.consultingHour', value: 100, crypto: 'USDT', icon: 'SupportAgentIcon' }, // USDC -> USDT
    { seller: 'WebWizards', itemKey: 'dealItem.websiteTemplate', value: 20, crypto: 'TON', icon: 'CodeIcon' }, // ETH -> TON
    { seller: 'GameStore', itemKey: 'dealItem.virtualItem', value: 5, crypto: 'TON', icon: 'ShoppingCartIcon' },
    { seller: 'MelodyMaker', itemKey: 'dealItem.musicTrack', value: 25, crypto: 'SOL', icon: 'MusicNoteIcon' },
    { seller: 'SkyHighTravel', itemKey: 'dealItem.flightTicket', value: 300, crypto: 'USDT', icon: 'FlightTakeoffIcon' }, // USDC -> USDT
    { seller: 'DealHunter', itemKey: 'dealItem.discountCoupon', value: 10, crypto: 'USDT', icon: 'LocalOfferIcon' },
    { seller: 'LearnChain', itemKey: 'dealItem.onlineCourse', value: 0.4, crypto: 'BNB', icon: 'SchoolIcon' }, // ETH -> BNB
    { seller: 'FixIT', itemKey: 'dealItem.repairService', value: 60, crypto: 'TON', icon: 'BuildIcon' },
    { seller: 'FitLife', itemKey: 'dealItem.fitnessPlan', value: 35, crypto: 'SOL', icon: 'FitnessCenterIcon' },
    { seller: 'SpeedyPizza', itemKey: 'dealItem.foodDelivery', value: 20, crypto: 'USDT', icon: 'FastfoodIcon' }, // USDC -> USDT
    { seller: 'BookWorm', itemKey: 'dealItem.ebook', value: 12, crypto: 'USDT', icon: 'BookIcon' }, // BTC -> USDT
    { seller: 'ArtisticSoul', itemKey: 'dealItem.customPortrait', value: 0.3, crypto: 'BNB', icon: 'PaletteIcon' }, // ETH -> BNB
    { seller: 'CodeSolutions', itemKey: 'dealItem.softwareLicense', value: 90, crypto: 'USDT', icon: 'ComputerIcon' },
    { seller: 'ShowTime', itemKey: 'dealItem.eventTicket', value: 75, crypto: 'TON', icon: 'EventIcon' },
    { seller: 'SecureContracts', itemKey: 'dealItem.securityAudit', value: 3.5, crypto: 'SOL', icon: 'SecurityIcon' }, // BTC -> SOL
    { seller: 'WebDomains', itemKey: 'dealItem.domainName', value: 18, crypto: 'SOL', icon: 'PublicIcon' },
    { seller: 'LegalEagles', itemKey: 'dealItem.legalAdvice', value: 200, crypto: 'USDT', icon: 'GavelIcon' }, // USDC -> USDT
    { seller: 'CryptoRealty', itemKey: 'dealItem.realEstateConsult', value: 350, crypto: 'USDT', icon: 'HomeWorkIcon' }, // ETH -> USDT
    { seller: 'Usernames Inc.', itemKey: 'dealItem.uniqueUsername', value: 50, crypto: 'TON', icon: 'LocalOfferIcon' },

  ], []);
  // -----------------------------------------------------

  useEffect(() => {
    let intervalId;
    let timeoutIdShow;
    let timeoutIdHide;

    const showRandomDeal = () => {
      // 1. Clear any pending hide timeouts
      clearTimeout(timeoutIdHide);
      // 2. Hide previous (start fade out animation)
      setIsVisible(false);

      // 3. Wait for fade out to roughly finish + pause before showing next
      timeoutIdShow = setTimeout(() => {
        const randomIndex = Math.floor(Math.random() * simulatedDeals.length);
        setCurrentDeal(simulatedDeals[randomIndex]);
        setIsVisible(true); // Show new (start fade in animation)

        // 4. Schedule the new one to hide later
        timeoutIdHide = setTimeout(() => {
          setIsVisible(false);
        }, 5000); // Notification display time (5 seconds)

      }, 600); // Pause before showing the next (0.6 seconds after fade out starts)
    };

    // Start the first show after an initial delay
    const initialTimeout = setTimeout(showRandomDeal, 3000); // Initial delay 3s

    // Set interval for subsequent shows
    intervalId = setInterval(showRandomDeal, 10000); // Interval between shows (10 seconds)

    // Cleanup on unmount
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutIdShow);
      clearTimeout(timeoutIdHide);
      clearTimeout(initialTimeout);
    };
  }, [simulatedDeals]);

  if (!currentDeal) {
    return null;
  }

  const displayValue = currentDeal.cryptoValue
    ? `${currentDeal.cryptoValue} ${currentDeal.crypto}`
    : (currentDeal.crypto === 'BNB' || currentDeal.crypto === 'SOL') // Форматируем малые значения
      ? `${currentDeal.value.toFixed(currentDeal.crypto === 'BNB' ? 3 : 2)} ${currentDeal.crypto}`
      : `~${currentDeal.value} ${currentDeal.crypto}`;

  // Get the Icon component based on the key
  const IconComponent = iconMap[currentDeal.icon] || ReceiptLongIcon;

  // Собираем текст, используя t() с интерполяцией
  const itemText = t(`common.${currentDeal.itemKey}`);
  const sellerText = currentDeal.seller; // Берем имя продавца напрямую
  const notificationMessage = t('common.activityFeed.message', {
    seller: sellerText,
    item: itemText,
    value: displayValue
  });

  return (
    <FeedWrapper>
      <NotificationBox $isVisible={isVisible}>
        <IconComponent />
        {/* Отображаем собранный текст */}
        <NotificationText dangerouslySetInnerHTML={{ __html: notificationMessage }} />
      </NotificationBox>
    </FeedWrapper>
  );
};

export default ActivityFeed; 